import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as StripeLogo } from 'assets/icons/stripe.svg';
import CommonButton from 'components/CommonButton/CommonButton';
import { renderToast } from 'components/Toast/Toast';
import useStripeStatus from 'hooks/useStripeStatus';
import axiosInstance from 'apis/axiosInstance';
import { IStripeLinkResponse } from 'interfaces/stripe';
import { isStripeSetupComplete } from 'utils/isStripeSetupComplete';

interface StripeIntegrationCardProps {}

const StripeIntegrationCard: FC<StripeIntegrationCardProps> = () => {
  const { data: status, isLoading, mutate } = useStripeStatus();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('return') || searchParams.get('refresh')) {
      mutate();
      renderToast({
        type: 'info',
        message: 'Updating your Stripe connection status…',
      });
      searchParams.delete('return');
      searchParams.delete('refresh');
      setSearchParams(searchParams, { replace: true });
    }
  }, [mutate, searchParams, setSearchParams]);

  const isConnected = isStripeSetupComplete(status);

  const isIncomplete = status?.hasAccount && !isConnected;

  const handleConnect = async () => {
    try {
      setIsRedirecting(true);
      const { data } = await axiosInstance.post<IStripeLinkResponse>(
        '/clinics/stripe/onboarding-link'
      );
      window.location.href = data.url;
    } catch (error) {
      setIsRedirecting(false);
      renderToast({
        type: 'error',
        message: 'Could not start Stripe onboarding. Please try again.',
      });
    }
  };

  const handleManage = async () => {
    try {
      setIsRedirecting(true);
      const { data } = await axiosInstance.get<IStripeLinkResponse>(
        '/clinics/stripe/login-link'
      );
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Could not open the Stripe dashboard. Please try again.',
      });
    } finally {
      setIsRedirecting(false);
    }
  };

  const renderStatus = () => {
    if (isConnected) {
      return (
        <div className="flex items-center gap-x-0.8">
          <span className="w-1.2 h-1.2 rounded-full bg-green" />
          <span className="text-14 leading-[2.1rem] text-green-secondary">
            Connected
          </span>
        </div>
      );
    }

    if (isIncomplete) {
      return (
        <div className="flex items-center gap-x-0.8">
          <span className="w-1.2 h-1.2 rounded-full bg-orange" />
          <span className="text-14 leading-[2.1rem] text-mid-grey">
            Setup incomplete
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-x-0.8">
        <span className="w-1.2 h-1.2 rounded-full bg-grey" />
        <span className="text-14 leading-[2.1rem] text-mid-grey">
          Not connected
        </span>
      </div>
    );
  };

  const renderAction = () => {
    if (isConnected) {
      return (
        <button
          className="text-14 font-bold text-magenta whitespace-nowrap"
          onClick={handleManage}
        >
          Manage in Stripe ↗
        </button>
      );
    }

    return (
      <CommonButton
        variant="secondary"
        isLoading={isRedirecting}
        onClick={handleConnect}
      >
        {isIncomplete ? 'Continue setup' : 'Connect Stripe'}
      </CommonButton>
    );
  };

  return (
    <div className="bg-white rounded-[1.6rem] border border-light-grey shadow-primary px-3 py-2.4">
      <div className="flex items-start justify-between gap-x-3">
        <div className="flex items-start gap-x-2">
          <div className="flex h-4.8 w-4.8 shrink-0 items-center justify-center rounded-[0.8rem] border border-light-grey">
            <StripeLogo className="w-4 h-auto" />
          </div>
          <div>
            <h4 className="text-20 font-bold leading-[3rem] text-darkest-grey">
              Stripe
            </h4>
            <p className="mt-0.4 max-w-[60rem] text-14 leading-[2.1rem] text-mid-grey">
              Collect deposits and save patient cards during online booking.
              Payments go directly to your clinic&apos;s bank account via Stripe
              — no existing Stripe account needed.
            </p>
            <div className="mt-1.6">
              {isLoading ? (
                <span className="text-14 leading-[2.1rem] text-mid-grey">
                  Checking status…
                </span>
              ) : (
                renderStatus()
              )}
            </div>
          </div>
        </div>
        <div className="shrink-0">{!isLoading && renderAction()}</div>
      </div>
    </div>
  );
};

export default StripeIntegrationCard;
